import { Link, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import CtaSection from "@/pages/home/components/CtaSection";
import { useServices } from "@/hooks/useSiteContent";

const formatPrice = (price: number) =>
  new Intl.NumberFormat("vi-VN").format(price);

export default function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: services, loading, error, reload } = useServices();
  const service = services.find((s) => s.slug === slug);

  if (loading) {
    return (
      <MainLayout>
        <div className="w-full min-h-[70vh] flex items-center justify-center bg-background-50">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="w-full min-h-[70vh] flex flex-col items-center justify-center bg-background-50 px-4 py-20 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
            <i className="ri-error-warning-line text-xl" />
          </div>
          <p className="text-sm text-foreground-600 font-body mb-4">{error}</p>
          <button
            onClick={reload}
            className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line mr-2" />
            Thử lại
          </button>
        </div>
      </MainLayout>
    );
  }

  if (!service) {
    return (
      <MainLayout>
        <div className="w-full min-h-[70vh] flex flex-col items-center justify-center px-4 py-20 bg-background-50">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-5">
            <i className="ri-error-warning-line text-2xl" />
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Không tìm thấy dịch vụ
          </h1>
          <p className="text-sm md:text-base text-foreground-600 text-center max-w-md mb-6 font-body">
            Dịch vụ bạn đang tìm không tồn tại hoặc đã bị gỡ bỏ.
          </p>
          <Link
            to="/services"
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors"
          >
            <i className="ri-arrow-left-line mr-2" />
            Xem tất cả dịch vụ
          </Link>
        </div>
      </MainLayout>
    );
  }

  const relatedServices = services.filter((s) => s.id !== service.id).slice(0, 3);

  return (
    <MainLayout>
      {/* Hero */}
      <section className="relative w-full h-[320px] md:h-[440px] overflow-hidden">
        <img
          src={service.image}
          alt={service.name}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/55" />
        <div className="relative w-full h-full flex flex-col items-center justify-center px-4 md:px-8 text-center">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-xs md:text-sm font-label text-background-200 mb-4"
          >
            <Link to="/" className="hover:text-background-50 transition-colors">
              Trang chủ
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to="/services"
              className="hover:text-background-50 transition-colors"
            >
              Dịch vụ
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50 font-medium">
              {service.shortName}
            </span>
          </nav>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-background-50 mb-4">
            {service.name}
          </h1>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-500 text-background-50 text-xs md:text-sm font-label font-semibold rounded-full whitespace-nowrap">
              <i className="ri-time-line" />
              {service.duration}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50/20 backdrop-blur-sm text-background-50 text-xs md:text-sm font-label font-medium rounded-full whitespace-nowrap">
              <i className="ri-shield-check-line" />
              An toàn - Không xâm lấn
            </span>
          </div>
        </div>
      </section>

      {/* Detail content */}
      <section className="w-full bg-background-50 py-14 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Main content */}
            <div className="w-full lg:w-[65%]">
              <h2 className="font-display text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Giới thiệu dịch vụ
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6 font-body">
                {service.description}
              </p>

              <h3 className="font-display text-lg md:text-xl font-bold text-foreground-950 mb-4">
                Lợi ích nổi bật
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {service.benefits.map((benefit) => (
                  <li
                    key={benefit}
                    className="flex items-start gap-2.5 bg-background-100 border border-background-200/70 rounded-lg px-4 py-3"
                  >
                    <div className="w-6 h-6 flex items-center justify-center text-primary-600 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-lg" />
                    </div>
                    <span className="text-sm text-foreground-800 font-body leading-snug">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h3 className="font-display text-base md:text-lg font-semibold text-foreground-950 mb-3">
                  <i className="ri-information-line text-primary-600 mr-2" />
                  Lưu ý khi trải nghiệm
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-foreground-600 font-body">
                    <i className="ri-arrow-right-line text-primary-600 mt-0.5" />
                    Đến đúng giờ hẹn để không phải chờ đợi.
                  </li>
                  <li className="flex items-start gap-2 text-sm text-foreground-600 font-body">
                    <i className="ri-arrow-right-line text-primary-600 mt-0.5" />
                    Mặc trang phục thoải mái, thấm hút tốt.
                  </li>
                  <li className="flex items-start gap-2 text-sm text-foreground-600 font-body">
                    <i className="ri-arrow-right-line text-primary-600 mt-0.5" />
                    Uống đủ nước trước và sau liệu trình.
                  </li>
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="w-full lg:w-[35%]">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 lg:sticky lg:top-24">
                <div className="flex items-end gap-2 mb-4">
                  <span className="font-display text-3xl font-bold text-primary-600">
                    {formatPrice(service.promoPrice)}đ
                  </span>
                  <span className="text-sm text-foreground-400 line-through font-body">
                    {formatPrice(service.price)}đ
                  </span>
                </div>
                <p className="text-xs text-foreground-500 mb-5 font-body">
                  Ưu đãi đặc biệt dành cho lần trải nghiệm đầu tiên.
                </p>

                <Link
                  to="/booking"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-semibold whitespace-nowrap rounded-md transition-colors mb-3"
                >
                  <i className="ri-calendar-check-line" />
                  Đặt lịch ngay
                </Link>
                <a
                  href="tel:0909123456"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-primary-300 text-primary-700 hover:bg-primary-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors"
                >
                  <i className="ri-phone-line" />
                  0909 123 456
                </a>

                <div className="border-t border-background-200/70 mt-6 pt-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className="ri-time-line" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 font-label">
                        Thời lượng
                      </p>
                      <p className="text-sm font-semibold text-foreground-900">
                        {service.duration}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className="ri-map-pin-line" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 font-label">
                        Địa điểm
                      </p>
                      <p className="text-sm font-semibold text-foreground-900">
                        Xanh Wellness - Q1, TP.HCM
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="w-full bg-background-100 py-14 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Dịch vụ liên quan
            </h2>
            <p className="text-sm md:text-base text-foreground-600 font-body">
              Khám phá thêm các liệu trình chăm sóc sức khỏe khác tại Xanh Wellness.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {relatedServices.map((related) => (
              <Link
                key={related.id}
                to={`/service/${related.slug}`}
                className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-colors"
              >
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={related.image}
                    alt={related.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-label font-semibold rounded-md whitespace-nowrap">
                      {related.duration}
                    </span>
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="font-display text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-700 transition-colors">
                    {related.shortName}
                  </h3>
                  <div className="flex items-end gap-2">
                    <span className="font-display text-lg font-bold text-primary-600">
                      {formatPrice(related.promoPrice)}đ
                    </span>
                    <span className="text-xs text-foreground-400 line-through font-body">
                      {formatPrice(related.price)}đ
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </MainLayout>
  );
}